import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";

import styles from "./Spine.module.css";

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Tracks which `[data-marked="true"]` descendants of `containerRef.current`
// are currently outside its scrollable viewport, in document order, plus
// which edge each is off on -- so a pill can show a count and a direction,
// and jump to "the next one" on each press. Re-observes whenever `deps`
// changes (typically: the view that produced the rows). Keys off the DOM
// nodes themselves rather than an id, since not every screen's schema has a
// stable one (Recalibration's doesn't) -- `jumpToRow` just scrolls the node.
export function useOffscreenMarkedRows(containerRef, deps) {
  const [offscreenMarked, setOffscreenMarked] = useState([]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const markedRows = Array.from(
      container.querySelectorAll('[data-marked="true"]'),
    );
    if (markedRows.length === 0) {
      setOffscreenMarked([]);
      return undefined;
    }

    const rowState = new Map(
      markedRows.map((row) => [row, { offscreen: true, direction: "down" }]),
    );

    const recompute = () => {
      setOffscreenMarked(
        markedRows
          .filter((row) => rowState.get(row).offscreen)
          .map((row) => ({ row, direction: rowState.get(row).direction })),
      );
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const direction =
            entry.rootBounds && entry.boundingClientRect.top < entry.rootBounds.top
              ? "up"
              : "down";
          rowState.set(entry.target, { offscreen: !entry.isIntersecting, direction });
        });
        recompute();
      },
      { root: container, threshold: 0 },
    );

    // No eager recompute() here: IntersectionObserver already fires once for
    // each newly observed target with its real measurement, so calling this
    // first (before that fires) would flash a wrong "all offscreen" count.
    markedRows.forEach((row) => observer.observe(row));

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return offscreenMarked;
}

export const jumpToRow = (row) => {
  row?.scrollIntoView({
    block: "center",
    behavior: prefersReducedMotion() ? "auto" : "smooth",
  });
};

export const ChangePill = ({ direction, count, onClick }) => (
  <div
    className={`${styles.changePillRow} ${direction === "up" ? styles.changePillUp : ""}`}
  >
    <button type="button" className={styles.changePill} onClick={onClick}>
      {direction === "up" ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
      {count} change{count > 1 ? "s" : ""}
    </button>
  </div>
);
